import { assertIsDefined, isDefined, isKeyOf, isNotDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { ArkError, type } from 'arktype';
import Papa from 'papaparse';
import { useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { selectAllCurrencyPairs } from '@/api/config/selectors.ts';
import { useCheckBulkTradeWithTradeCapture } from '@/App/BulkTrade/BulkTradeContext.tsx';
import { SgIcon } from '@/components/sg-design-system/components/display/SgIcon';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { CacheProofLink } from '@/components/ui/CacheProofLink.tsx';
import { UploadFile } from '@/components/ui/UploadFile.tsx';
import { selectBulkTrade } from '@/store/bulkTrade/selectors.ts';
import type { BulkCashLeg, BulkLeg, BulkSwapLeg } from '@/store/bulkTrade/state.ts';
import { changeBulkTradeCurrencyPair } from '@/store/bulkTrade/thunks/changeBulkTradeCurrencyPair.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectTabClientId } from '@/store/tabs/selectors.ts';

type BulkTradeImportModalProps = {
  tabId: string;
  onClose(): void;
};

export const BulkTradeImportModal = (props: BulkTradeImportModalProps) => {
  const { tabId, onClose } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { sgwtHelpCenter } = useSgwtWidgets();

  const dispatch = useAppDispatch();
  const checkBulkTradeWithTradeCapture = useCheckBulkTradeWithTradeCapture();

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [crashMessage, setCrashMessage] = useState<string | undefined>(undefined);

  const clientId = useAppSelector((state) => selectTabClientId(state, tabId));

  const data = useAppSelector((state) => {
    const allCurrencyPairs = selectAllCurrencyPairs(state);
    const bulkTrade = selectBulkTrade(state, tabId);

    if (isNotDefined(bulkTrade)) {
      return {
        allCurrencyPairs,
      };
    }

    return {
      bulkTradeId: bulkTrade.id,
      nextLegId: bulkTrade.nextLegId,
      clientId: selectTabClientId(state, tabId),
      allCurrencyPairs,
    };
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Do something with the files

    setSelectedFile(acceptedFiles?.[0]);
    setErrorMessage(undefined);
  }, []);

  const onImport = async () => {
    if (isNotDefined(selectedFile)) {
      return;
    }

    try {
      const content = await loadBulkTradeFromFile(selectedFile, data.allCurrencyPairs);

      if (content.success === false) {
        sgwtWebAnalytics?.trackEvent('BulkTrade', 'import failed', content.reason);

        setCrashMessage(`${content.reason} on line ${content.rowNumber}.`);

        return;
      }

      const bulkTradeId = data?.bulkTradeId ?? uuid();
      const nextLegId = data?.nextLegId ?? 0;

      const currencyPair = content.rows[0]?.currencyPair;

      if (isNotDefined(currencyPair) || isNotDefined(clientId)) {
        sgwtWebAnalytics?.trackEvent('BulkTrade', 'import failed', 'missing currency pair and client context');

        setErrorMessage(
          'Currency pair is missing in the first row of the file, and no bulk trade is currently open to infer it from',
        );

        return;
      }

      sgwtWebAnalytics?.trackEvent('BulkTrade', 'import success', currencyPair);

      dispatch(changeBulkTradeCurrencyPair({ tabId, bulkTradeId, currencyPair }));

      const legChanges = content.rows.reduce(
        (acc, row, rowIndex) => {
          acc[String(nextLegId + rowIndex)] =
            row.type === 'Swap'
              ? ({
                  id: String(nextLegId + rowIndex),
                  type: 'Swap',

                  way: row.clientWay === 'Buy/Sell' ? 'BuyThenSell' : 'SellThenBuy',

                  // historical type is not used for swap legs in bulk trades
                  isUneven: isDefined(row.amount2) && row.amount2 !== '',

                  // === NEAR VALUES

                  nearNotional: amountValueWithAccelerator(row.amount1),
                  nearExpiryDate: row.date1,

                  // === FAR VALUES
                  farNotional: isDefined(row.amount2) ? amountValueWithAccelerator(row.amount2) : undefined, // = nearNotional unless isUneven
                  farExpiryDate: row.date2,
                } satisfies BulkSwapLeg)
              : ({
                  id: String(nextLegId + rowIndex),
                  type: row.type,

                  way: row.clientWay,

                  notional: amountValueWithAccelerator(row.amount1),
                  maturityDate: row.date1,
                } satisfies BulkCashLeg);

          return acc;
        },
        {} as Record<string, Partial<BulkLeg>>,
      );

      checkBulkTradeWithTradeCapture({
        bulkTradeId: bulkTradeId,
        currencyPair,
        negotiatedCurrencyIndex: 1,
        legChanges,
      });

      onClose();
    } catch (error) {
      if (error instanceof BulkTradeImportParsingError) {
        sgwtWebAnalytics?.trackEvent('BulkTrade', 'import failed', 'bad file format with row errors');

        const userMessage = error.rowErrors.map(({ rowNumber, reason }) => `Line ${rowNumber}: ${reason}`).join('\n');

        setErrorMessage(userMessage);
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : 'An unknown error occurred while importing the file';

      logger.logError(
        'Unexpected error while importing bulk trade file: {errorMessage_s} {errorStack_s}',
        errorMessage,
        error instanceof Error ? error.stack : undefined,
      );

      sgwtWebAnalytics?.trackEvent('BulkTrade', 'import failed', errorMessage);

      setErrorMessage(errorMessage);
    }
  };

  const openHelp = useCallback(() => sgwtHelpCenter?.topic('bulk.import'), [sgwtHelpCenter]);

  const importLinks = useMemo(
    () => ({
      help: (
        // oxlint-disable-next-line anchor-is-valid -- This is a link to help center. It is not a navigation link.
        <a
          href="#"
          onClick={(e) => {
            sgwtWebAnalytics?.trackEvent('BulkTrade', 'open import help topic');
            e.preventDefault();
            openHelp();
          }}
        >
          <FormattedMessage id="bulkOrder.import.help" tagName="u" />
        </a>
      ),

      link: (
        <CacheProofLink href={'/samples/BulkTradeExample.csv'} fileName={'SGME-FX-BulkTrade.csv'}>
          <FormattedMessage id="bulkTrade.importData.helpTopicLink" tagName="u" />
        </CacheProofLink>
      ),
    }),
    [openHelp, sgwtWebAnalytics],
  );

  if (isDefined(crashMessage)) {
    return (
      <Modal show={true} onHide={onClose} backdrop="static">
        <ModalHeader>
          <Modal.Title className="text-danger text-center w-100">
            <span className="text-danger rounded-circle bg-danger bg-opacity-10 p-3">
              <SgIcon icon="warning" />
            </span>
            <h3 className="mt-24px">Import failed</h3>
          </Modal.Title>
        </ModalHeader>

        <ModalBody>
          <div className="text-secondary">
            Your file has not been accepted by our system. Please verify the information. Your file must be in CSV
            format and have the proper table headings.
          </div>

          <div className="text-danger bg-danger bg-opacity-10 p-3 mt-24px" data-e2e="import-error-message">
            {crashMessage}
          </div>
        </ModalBody>

        <ModalFooter>
          <SgButton onClick={onClose} data-e2e="import-close-error-button">
            Close
          </SgButton>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal show={true} onHide={onClose} backdrop="static">
      <ModalHeader closeButton>
        <Modal.Title>
          <FormattedMessage id="bulkTrade.importData.title" />
        </Modal.Title>
      </ModalHeader>

      <ModalBody>
        <FormattedMessage
          id="bulkTrade.importData.message"
          values={{ helpTopicLink: importLinks.help, sampleFileLink: importLinks.link }}
        />

        <div className="mt-24px">
          <UploadFile
            selectedFile={selectedFile}
            onDrop={onDrop}
            errorMessage={errorMessage}
            data-e2e="import-file-input"
          />
        </div>
      </ModalBody>

      <ModalFooter>
        <HStack gap="8px" horizontalAlignment="end">
          <SgButton purpose="flat" onClick={onClose} data-e2e="cancel-import-button">
            <FormattedMessage id="bulkTrade.importData.buttons.cancel" />
          </SgButton>

          <SgButton
            onClick={onImport}
            disabled={isNotDefined(selectedFile) || isDefined(errorMessage)}
            data-e2e="import-file-button"
          >
            <FormattedMessage id="bulkTrade.importData.buttons.import" />
          </SgButton>
        </HStack>
      </ModalFooter>
    </Modal>
  );
};

export type BulkTradeImportCash = {
  type: 'Spot' | 'Forward';
  clientWay: 'Buy' | 'Sell';
  currencyPair: string;
  negociatedCurrency: string;
  amount1: string;
  date1: string; // date or tenor
};

export type BulkTradeImportSwap = {
  type: 'Swap';
  clientWay: 'Buy/Sell' | 'Sell/Buy';
  currencyPair: string;
  negociatedCurrency: string;
  amount1: string;
  date1: string; // date or tenor
  amount2: string | null; // if null => even, if not null => uneven
  date2: string; // date or tenor
};

export type BulkTradeImportData = BulkTradeSuccessImport | BulkTradeFailedImport;

type BulkTradeSuccessImport = {
  success: true;
  rows: (BulkTradeImportCash | BulkTradeImportSwap)[];
};

type BulkTradeFailedImport = {
  success: false;
  rowNumber: number;
  reason: string;
};

/**
 * User-facing parsing error that includes CSV row details.
 */
class BulkTradeImportParsingError extends Error {
  public readonly rowErrors: ReadonlyArray<{ rowNumber: number; reason: string }>;

  /**
   * @param rowErrors list of parsing issues indexed by source file row number
   */
  constructor(rowErrors: ReadonlyArray<{ rowNumber: number; reason: string }>) {
    super('Invalid bulk trade file');
    this.name = 'BulkTradeImportParsingError';
    this.rowErrors = rowErrors;
  }
}

const BulkTradeImportCashRowType = type([
  "'SPOT' | 'FORWARD'",
  "'Buy' | 'Sell'",
  'string', // currency pair
  'string', // amount 1 -> number or accelerator (1M)
  'string', // currency
  'string', // date or tenor 1
  "''",
  "''",
]);

const BulkTradeImportSwapRowType = type([
  "'SWAP'",
  "'Buy/Sell' | 'Sell/Buy'",
  'string', // currency pair
  'string', // amount 1 -> number or accelerator (1M)
  'string', // currency
  'string', // date or tenor 1
  'string | null', // amount 2 -> number or accelerator (1M) - if null => even, if not null => uneven
  'string', // date or tenor 2
]);

const BulkTradeImportRowType = BulkTradeImportCashRowType.or(BulkTradeImportSwapRowType);

type BulkTradeImportCashRow = typeof BulkTradeImportCashRowType.infer;
type BulkTradeImportSwapRow = typeof BulkTradeImportSwapRowType.infer;

async function loadBulkTradeFromFile(file: File, allCurrencyPairs: string[]): Promise<BulkTradeImportData> {
  // CSV
  if (file.name.endsWith('.csv')) {
    const content = await file.text();

    return await new Promise<BulkTradeImportData>((resolve, reject) => {
      Papa.parse(content, {
        skipEmptyLines: true,
        skipFirstNLines: 1,
        // dynamicTyping: true,
        // header: true,
        download: false,

        complete: (result) => {
          const rawData = BulkTradeImportRowType.array()(result.data);

          const rowErrors: Array<{ rowNumber: number; reason: string }> = [];

          const successImportData: BulkTradeSuccessImport = {
            success: true,
            rows: [],
          };

          for (const [index, row] of rawData.entries()) {
            const rowNumber = index + 2;

            if (row instanceof ArkError) {
              rowErrors.push({
                rowNumber,
                reason: formatArkTypeError(row),
              });

              continue;
            }

            const result =
              row[0] === 'SPOT' || row[0] === 'FORWARD'
                ? processCashRow(row as BulkTradeImportCashRow, rowNumber, allCurrencyPairs)
                : processSwapRow(row as BulkTradeImportSwapRow, rowNumber, allCurrencyPairs);

            if (!result.success) {
              resolve(result);
              return;
            }

            successImportData.rows.push(result.row);
          }

          if (rowErrors.length > 0) {
            reject(new BulkTradeImportParsingError(rowErrors));
            return;
          }

          resolve(successImportData);
        },

        // @ts-expect-error - PapaParse typings are wrong, error is of type Error, not string
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  // Excel XLSX
  if (file.name.endsWith('.xlsx')) {
    throw new Error('XLSX parsing not implemented yet');
  }

  throw new Error('Unsupported file type');
}

/**
 * Processes a SPOT or FORWARD row and validates the currency pair.
 *
 * @param row parsed cash row tuple
 * @param rowNumber 1-based row number for error reporting
 * @param allCurrencyPairs list of valid currency pairs
 * @returns the parsed cash row data, or a failed import if the currency pair is invalid
 */
function processCashRow(
  row: BulkTradeImportCashRow,
  rowNumber: number,
  allCurrencyPairs: string[],
): { success: true; row: BulkTradeImportCash } | BulkTradeFailedImport {
  const [type, clientWay, currencyPair, amount1, negociatedCurrency, date1] = row;

  if (!allCurrencyPairs.includes(currencyPair)) {
    return {
      success: false,
      rowNumber,
      reason: `Invalid currency pair '${currencyPair}'`,
    };
  }

  return {
    success: true,
    row: {
      type: type === 'SPOT' ? 'Spot' : 'Forward',
      clientWay,
      currencyPair,
      amount1,
      negociatedCurrency,
      date1,
    },
  };
}

/**
 * Processes a SWAP row and validates the currency pair.
 *
 * @param row parsed swap row tuple
 * @param rowNumber 1-based row number for error reporting
 * @param allCurrencyPairs list of valid currency pairs
 * @returns the parsed swap row data, or a failed import if the currency pair is invalid
 */
function processSwapRow(
  row: BulkTradeImportSwapRow,
  rowNumber: number,
  allCurrencyPairs: string[],
): { success: true; row: BulkTradeImportSwap } | BulkTradeFailedImport {
  const [, clientWay, currencyPair, amount1, negociatedCurrency, date1, amount2, date2] = row;

  if (!allCurrencyPairs.includes(currencyPair)) {
    return {
      success: false,
      rowNumber,
      reason: `Invalid currency pair '${currencyPair}'`,
    };
  }

  return {
    success: true,
    row: {
      type: 'Swap',
      clientWay,
      currencyPair,
      amount1,
      negociatedCurrency,
      date1,
      amount2,
      date2,
    },
  };
}

/**
 * Formats ArkType validation errors into a user-readable reason.
 *
 * @param error ArkType validation error
 * @returns concise validation reason for UI display
 */
function formatArkTypeError(error: ArkError): string {
  const formattedError = `${error}`;

  if (formattedError.trim().length > 0) {
    return formattedError;
  }

  return 'Invalid row format';
}

function amountValueWithAccelerator(amount: string): number {
  const acceleratorMultipliers: Record<'K' | 'M' | 'B', number> = {
    K: 1_000,
    M: 1_000_000,
    B: 1_000_000_000,
  };

  const match = amount.match(/^([\d,.]+)\s*([KMB])?$/i);

  if (!match) {
    return Number(amount);
  }

  const integerPart = match[1];
  assertIsDefined(integerPart, 'the numeric part of the regex is a mandatory capturing group');

  const numericPart = Number.parseFloat(integerPart.replace(/,/g, ''));
  const accelerator = match[2]?.toUpperCase();

  const multiplier =
    accelerator && isKeyOf(accelerator, acceleratorMultipliers) ? acceleratorMultipliers[accelerator] : 1;

  return numericPart * multiplier;
}
